import React, { useEffect, useState } from 'react';
import { TextField, Autocomplete, FormLabel } from '@mui/material';
import useApiControl from '../../../common/useApiControl';
import UserUpdateDialog from '../../dialogs/UserUpdateDialog';
import BaseForm from '../BaseForm';

function UserEditForm({ user, masterTagsData }) {
  const initialThumbnailName = user.thumbnail
    ? user.thumbnail.split('\\').pop().split('/').pop()
    : '';
  const canCreateRooms = Number(user.owner_permission ?? 0) > 0;
  const ownerCreateLimit = Number(user.owner_create_limit ?? 0);

  const [form, setForm] = useState({
    name: user.name,
    email: user.email || '',
    description: user.description,
    ticket: user.ticket,
    favorite: [],
    thumbnail: initialThumbnailName,
  });

  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [tagsValue, setTagsValue] = useState([]);
  const [masterTagOptions, setMasterTagOptions] = useState([]);
  const [userUpdateDialogVisible, setUserUpdateDialogVisible] = useState(false);

  const handleDialogClose = () => {
    setUserUpdateDialogVisible(false);
    window.location.reload();
  };

  useEffect(() => {
    if (masterTagsData?.tags) {
      const options = masterTagsData.tags.map((tag) => ({
        label: tag.name,
        value: tag.id,
      }));
      setMasterTagOptions(options);

      const initialFavorite = JSON.parse(user.favorite || '[]');
      const selected = initialFavorite
        .map((id) => options.find((opt) => opt.value === id))
        .filter(Boolean);
      setTagsValue(selected);
      setForm((prev) => ({ ...prev, favorite: initialFavorite }));
    }
  }, [masterTagsData, user.favorite]);

  const { error, executeRequest: updateUser } = useApiControl({
    action: '/update_user',
    params: form,
    method: 'POST',
  });

  const handleChange = (key) => (e) => {
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
  };

  const tagsHandleChange = (event, value) => {
    setTagsValue(value);
    setForm((prev) => ({
      ...prev,
      favorite: value.map((opt) => opt.value),
    }));
  };

  const handleSubmit = () => {
    const formData = new FormData();

    formData.append('name', form.name);
    formData.append('email', form.email || '');
    formData.append('description', form.description || '');
    formData.append('ticket', form.ticket || '');

    form.favorite.forEach((value) => {
      formData.append('favorite[]', value);
    });

    if (thumbnailFile) {
      formData.append('thumbnail', thumbnailFile);
    }

    updateUser(formData).then(() => {
      setUserUpdateDialogVisible(true);
    });
  };

  return (
    <>
      <UserUpdateDialog
        isVisible={userUpdateDialogVisible}
        onClose={handleDialogClose}
      />
      <h2>プロフィール編集</h2>
      <BaseForm
        onSubmit={handleSubmit}
        submitLabel="保存"
        logMessage="user Update Api start"
        apiError={error}
      >
        <TextField
          label="ユーザー名"
          value={form.name}
          onChange={handleChange('name')}
          inputProps={{ maxLength: 20 }}
        />

        <TextField
          label="メールアドレス"
          type="email"
          value={form.email}
          onChange={handleChange('email')}
        />

        {!canCreateRooms && (
          <FormLabel component="legend">
            メールアドレスとパスワードを登録するとルームの作成ができるようになります。
          </FormLabel>
        )}

        <TextField
          label="詳細説明"
          value={form.description}
          onChange={handleChange('description')}
          multiline
          rows={4}
          inputProps={{ maxLength: 800 }}
        />

        <Autocomplete
          multiple
          options={masterTagOptions}
          value={tagsValue}
          getOptionLabel={(option) => option.label}
          onChange={tagsHandleChange}
          renderInput={(params) => (
            <TextField {...params} label="お気に入りタグ" variant="outlined" />
          )}
        />

        <div>
          <div style={{ marginBottom: '8px' }}>サムネイル画像</div>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => {
              const file = e.target.files?.[0];
              setThumbnailFile(file || null);
              if (file) {
                setForm((prev) => ({ ...prev, thumbnail: file.name }));
              }
            }}
          />
          {form.thumbnail && (
            <div style={{ marginTop: '8px', fontSize: '14px' }}>
              現在のファイル: {form.thumbnail}
            </div>
          )}
        </div>
      </BaseForm>
    </>
  );
}

export default UserEditForm;
